<template>
    <div class="c-gd-psla-calculator">
        <h2>Mesothelioma General Damages (PSLA) Calculator</h2>

        <p>
            Welcome to our Mesothelioma General Damages (PSLA) calculator. The calculator shows estimated common law awards
            according to 'age' and 'duration of symptoms' &mdash; the period between onset of the disease
            and death (or anticipated date of death).
        </p>

        <p>
            There are two parts to the calculator:
        </p>

        <ol>
            <li>
                The first is a Tariff Table designed to give a broad brush indication of possible awards and
                is not intended to substitute a detailed review and consideration of actual awards to
                properly value any PSLA award. The Tariff Table is based on the 14th Edition of the JC Guidelines.
            </li>
            <li>
                The second shows actual PLSA awards which most closely fit the selected age and duration
                of symptoms.
            </li>
        </ol>

        <p>
            Onset of symptoms:
            <strong>
                {{ onsetOfSymptoms }}
            </strong>
        </p>
        
        <p>
            Duration of symptoms:
            <strong>
                {{ calculatedDurationOfSymptoms }} month{{ calculatedDurationOfSymptoms === 1 ? '' : 's' }}
            </strong>
        </p>

        <p>
            Age at Death: <strong>{{ ageAtDeath }}</strong>
        </p>

        <label>
            Duration of Symptoms
        </label>

        <div class="c-gd-psla-calculator__slider p-dependency-calc__slider">
            <vue-slider ref="slider" :min="0" :max="37"
                        :real-time="true"
                        :clickable="true"
                        v-model="calculatedDurationOfSymptoms"
                        :piecewise="true"
                        :process-style="{'backgroundColor' : '#4A89E5'}"
                        :tooltip-style="{'backgroundColor' : '#4A89E5', 'borderColor' : '#4A89E5'}"
                        :formatter.string="calculatedDurationOfSymptoms + ' months'"
                        :label-style="{'color' : '#fff'}"></vue-slider>
        </div>

        <div class="u-d-flex u-flex-space-between">
            <h2>Tariff Table</h2>

            <button class="a-button a-button--smaller" @click="applyUplift()">
                {{ upliftButton ? 'Add' : 'Remove' }} 10% Uplift
            </button>
        </div>

        <div class="a-table--short-no-style">
            <table class="a-table a-table--full-width a-table--condensed" v-if="table[50] && !loading.table">
                <tr>
                    <th rowspan="2">Age (Up to)</th>
                    <th :colspan="table[50].length" style="text-align: center">
                        Duration (months)
                    </th>
                </tr>
                <tr>
                    <th v-for="col in table[50].length">
                        <span v-if="col===1">0-3</span>
                        <span v-else-if="col===13">37+</span>
                        <span v-else>{{ col * 3 - 2 }}-{{ col * 3 }}</span>
                    </th>
                </tr>
                <tr v-for="(tr, age) in table">
                    <th>{{ age }}</th>
                    <td v-for="(td, index) in tr" :style="getTariffTableTdStyle(index, age)" @click="usePslaAward(uplift ? Math.round(td*1.1) : td)">
                        {{ currencyFormat(uplift ? Math.round(td*1.1) : td, '£', 0) }}
                    </td>
                </tr>
            </table>
        </div>
        <div v-if="loading.table">
            Loading... one moment
        </div>
        <p class="c-gd-psla-calculator__disclaimer">Data is up-to-date as of {{ currentRpi }} *</p>

        <h2>Awards</h2>
        <psla-award v-for="award in filteredAwards" :award="award" v-if="!loading.awards"></psla-award>
        <div v-if="loading.awards">
            Loading... one moment
        </div>
    </div>
</template>
<script>
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error';
    import DateInput from 'components/form/control/DateInput';
    import FormValidator from 'components/form/validator/mixin';
    import Modal from 'components/modal/Modal';
    import Moment from 'moment';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import {mapGetters, mapActions} from 'vuex';
    import ModalContent from 'components/modal/ModalContent';
    import {validDate} from 'components/form/validator/validations';
    import currencyFormat from 'mixins/currencyFormat';
    import VueSlider from 'vue-slider-component';
    import PslaAward from 'pages/fatal-accident-claims/dependency/steps/general-damages/PslaAward';

    /**
     * Used to determine the life expectancy from a given "person"
     */
    export default {
        name: 'general-damages-psla-calculator',
        data() {
            return {
                calculatedDurationOfSymptoms: 0,
                loading: {
                    table: false,
                    awards: false
                },
                upliftButton: true,
                uplift: false,
            }
        },
        computed: {
            ...mapGetters('fatal/dependency/deceased', {theDeceased: 'theDeceased'}),
            ...mapGetters('psla', {pslaValue: 'value'}),
            table: {
                get() {
                    return this.pslaValue('table');
                }
            },
            awards: {
                get() {
                    return this.pslaValue('awards');
                }
            },
            filteredAwards() {
                if (!this.awards.length) {
                    return [];
                }
                return this.awards
                    //Generate a score depending on how far they are away from our person
                    .map(award => {
                        let score = Math.abs(this.ageAtDeath - award.age) +
                            Math.abs(this.calculatedDurationOfSymptoms - award.duration);
                        return {score, award};
                    })
                    // Sort into score order
                    .sort((a, b) => {
                        if (a.score === b.score) {
                            return 0;
                        }
                        return a.score - b.score;
                    })
                    // Return just the awards
                    .map(info => info.award)
                    // Top 10 awards
                    .slice(0, 10);
            },
            dateOfBirth: {
                get() {
                    return this.theDeceased.dateOfBirth;
                }
            },
            ageAtDeath: {
                get() {
                    return this.theDeceased.ageAtDeath;
                }
            },
            upperAge: {
                get() {
                    let upperAge = this.ages.reverse().filter(age => age <= this.ageAtDeath);

                    if (!upperAge || upperAge.length < 1) {
                        return this.ages.reverse()[0];
                    }

                    return upperAge[0];
                }
            },
            ages: {
                get() {
                    return Object.keys(this.table);
                }
            },
            dateOfDeath: {
                get() {
                    return this.theDeceased.dateOfDeath;
                }
            },
            onsetOfSymptoms: {
                get() {
                    return this.theDeceased.onsetOfSymptoms;
                }
            },
            durationOfSymptoms: {
                get() {
                    return Moment(this.dateOfDeath, 'DD/MM/YYYY').diff(Moment(this.onsetOfSymptoms, 'DD/MM/YYYY'), 'months');
                },
            },
            currentRpi: {
                get() {
                    return this.pslaValue('currentRpi');
                }
            }
        },
        components: {
            Control,
            ControlError,
            DateInput,
            Modal,
            ModalContent,
            VueSlider,
            PslaAward
        },
        mixins: [
            FormValidator,
            currencyFormat
        ],
        created() {
            this.loadTableCheck();
            this.loadAwardsCheck(); // TODO: Only load this when modal is opened rather than when step is loaded.
            this.calculatedDurationOfSymptoms = this.durationOfSymptoms;
            this.loadCurrentRpi();
        },
        methods: {
            ...mapActions('psla', ['loadTable', 'loadAwards', 'loadCurrentRpi']),
            ...mapActions('fatal/dependency/headsOfLoss', {updateValue: 'updateValue'}),
            ...mapActions('modal', ['toggle']),
            loadTableCheck() {
                if (!this.table.length) {
                    this.loading.table = true;
                    this.loadTable().then(() => {
                        this.loading.table = false;
                    });
                }
            },
            loadAwardsCheck() {
                if (!this.awards.length) {
                    this.loading.awards = true;
                    this.loadAwards().then(() => {
                        this.loading.awards = false;
                    });
                }
            },
            isExactMatch(index, age) {
                return (this.isColMatch(index) && this.isRowMatch(age));
            },
            isColMatch(index) {
                index++;

                if (index === 1 && this.calculatedDurationOfSymptoms <= 3) {
                    return true;
                }

                if (index === 13 && this.calculatedDurationOfSymptoms >= 37) {
                    return true;
                }

                return (this.calculatedDurationOfSymptoms >= index * 3 - 2 && this.calculatedDurationOfSymptoms <= index * 3);
            },
            isRowMatch(age) {
                return (this.upperAge === age);
            },
            getTariffTableTdStyle(index, age) {
                if (this.isExactMatch(index, age)) {
                    return 'cursor: pointer; background:#c5ecfe; font-weight: bold; font-size: 14px;';
                }

                if (this.isColMatch(index) || this.isRowMatch(age)) {
                    return 'cursor: pointer; background:#c5ecfe; font-size: 14px;';
                }

                return 'cursor: pointer; font-size: 14px;';
            },
            applyUplift() {
                this.upliftButton = !this.upliftButton;
                this.uplift = !this.uplift;
            },            
            usePslaAward(value) {
                this.updateValue({key: 'generalDamages', value});
                this.toggle('generalDamagesPslaModal');
            },
        }
    }
</script>


// WEBPACK FOOTER //
// GeneralDamagesPslaCalculator.vue?a3c6d766